import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeUserToPush, unsubscribeUserFromPush } from '../utils/pushNotificationManager';
import { CircularProgress } from '@mui/material';
import FiberLoader from '../components/common/FiberLoader';

const ClientAuthContext = createContext(null);

export const ClientAuthProvider = ({ children }) => {
    const [client, setClient] = useState(null);
    const [clientToken, setClientToken] = useState(null);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const navigate = useNavigate();

    // Verificar autenticación al cargar el componente
    useEffect(() => {
        const checkClientAuth = () => {
            console.log('ClientAuthContext - Verificando autenticación');
            const token = localStorage.getItem('clientToken');
            const storedClient = localStorage.getItem('clientData');
            const storedServices = localStorage.getItem('clientServices');

            if (!token || !storedClient) {
                console.log('ClientAuthContext - No hay token o datos de cliente');
                localStorage.removeItem('clientToken');
                localStorage.removeItem('clientData');
                localStorage.removeItem('clientServices');
                setClient(null);
                setClientToken(null);
                setServices([]);
                setLoading(false);
                return;
            }

            try {
                const clientData = JSON.parse(storedClient);
                console.log('ClientAuthContext - Cliente autenticado:', clientData.fullName, 'Estado:', clientData.status);
                setClient(clientData);
                setClientToken(token);
                
                if (storedServices) {
                    setServices(JSON.parse(storedServices));
                } else {
                    // Cargar servicios del backend como respaldo
                    fetch('/api/client/auth/services', {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                    .then(res => res.json())
                    .then(data => {
                        if (Array.isArray(data)) {
                            setServices(data);
                            localStorage.setItem('clientServices', JSON.stringify(data));
                        }
                    })
                    .catch(err => console.error('Error fetching fallback services:', err));
                }
                setAuthError(null);
                
                // Suscribir automáticamente a notificaciones Web Push si está permitido
                subscribeUserToPush().catch(err => console.error('Error auto-suscribiendo notificaciones push:', err));
            } catch (error) {
                console.error('ClientAuthContext - Error al parsear datos del cliente:', error);
                localStorage.removeItem('clientToken');
                localStorage.removeItem('clientData');
                localStorage.removeItem('clientServices');
                setClient(null);
                setClientToken(null);
                setServices([]);
                setAuthError('Error en datos de sesión. Por favor, inicie sesión nuevamente.');
            }
            setLoading(false);
        };

        checkClientAuth();
    }, []);

    const loginClient = useCallback(async (username, password) => {
        try {
            console.log('ClientAuthContext - Intentando login con:', username);
            setLoading(true);
            setAuthError(null);

            const response = await fetch('/api/client/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password, allowInactive: true }), // Parámetro para permitir clientes inactivos
            });

            const data = await response.json();

            if (!response.ok) {
                console.log('ClientAuthContext - Error en login:', data.message);
                setAuthError(data.message || 'Error en el inicio de sesión');
                throw new Error(data.message || 'Error en el inicio de sesión');
            }

            console.log('ClientAuthContext - Login exitoso, datos recibidos:', {
                tokenRecibido: !!data.token,
                clienteRecibido: !!data.client,
                serviciosRecibidos: data.services?.length || 0,
                estado: data.client?.status
            });

            if (!data.token || !data.client) {
                throw new Error('Respuesta incompleta del servidor');
            }

            // Guardar token, datos del cliente y lista de servicios
            localStorage.setItem('clientToken', data.token);
            localStorage.setItem('clientData', JSON.stringify(data.client));
            localStorage.setItem('clientServices', JSON.stringify(data.services || []));

            // Actualizar estado
            setClientToken(data.token);
            setClient(data.client);
            setServices(data.services || []);
            setAuthError(null);

            // Suscribir automáticamente a notificaciones Web Push al iniciar sesión
            subscribeUserToPush().catch(err => console.error('Error suscribiendo notificaciones push en login:', err));

            console.log('ClientAuthContext - Redirigiendo a dashboard');

            // Usar setTimeout para asegurar que el estado se actualice antes de redirigir
            setTimeout(() => {
                navigate('/client/dashboard');
            }, 100);

            return { success: true, message: data.message };
        } catch (error) {
            console.error('ClientAuthContext - Error en login:', error);
            setAuthError(error.message || 'Error de conexión');
            return { error: error.message };
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const logoutClient = useCallback(() => {
        console.log('ClientAuthContext - Cerrando sesión');

        // Desuscribir de notificaciones push antes de borrar tokens
        unsubscribeUserFromPush().catch(err => console.error('Error desuscribiendo notificaciones push en logout:', err));

        // Notificar al servidor sobre el logout
        const token = localStorage.getItem('clientToken');
        if (token) {
            fetch('/api/client/auth/logout', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }).catch(error => {
                console.error('Error al cerrar sesión en el servidor:', error);
            });
        }

        // Limpiar almacenamiento local
        localStorage.removeItem('clientToken');
        localStorage.removeItem('clientData');
        localStorage.removeItem('clientServices');

        // Limpiar estado
        setClient(null);
        setClientToken(null);
        setServices([]);
        setAuthError(null);

        // Redireccionar
        navigate('/client/login');
    }, [navigate]);

    // Método para cambiar de servicio activo
    const switchActiveService = useCallback(async (targetClientId) => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/auth/switch-service', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ targetClientId }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al cambiar de servicio');
            }

            // Guardar token y datos del cliente nuevo
            localStorage.setItem('clientToken', data.token);
            localStorage.setItem('clientData', JSON.stringify(data.client));

            // Actualizar estado (los servicios se mantienen iguales porque comparten DNI)
            setClientToken(data.token);
            setClient(data.client);
            setAuthError(null);
            
            return { success: true };
        } catch (error) {
            console.error('ClientAuthContext - Error al cambiar de servicio:', error);
            setAuthError(error.message || 'Error al cambiar de servicio');
            return { error: error.message };
        }
    }, []);

    // Método para actualizar el alias del servicio
    const updateServiceAlias = useCallback(async (serviceId, newAlias) => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/auth/services/${serviceId}/alias`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ alias: newAlias }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al actualizar el nombre del servicio');
            }

            // Actualizar el estado de services localmente
            setServices(prevServices => {
                const updated = prevServices.map(srv => {
                    if (srv.id === serviceId) {
                        return { ...srv, alias: newAlias };
                    }
                    return srv;
                });
                localStorage.setItem('clientServices', JSON.stringify(updated));
                return updated;
            });

            // Si el servicio editado es el activo actualmente, actualizar client.alias
            setClient(prevClient => {
                if (prevClient && prevClient.id === serviceId) {
                    const updatedClient = { ...prevClient, alias: newAlias };
                    localStorage.setItem('clientData', JSON.stringify(updatedClient));
                    return updatedClient;
                }
                return prevClient;
            });

            return { success: true };
        } catch (error) {
            console.error('ClientAuthContext - Error al actualizar el alias del servicio:', error);
            return { error: error.message };
        }
    }, []);

    // Método para renovar token si es necesario
    const refreshToken = useCallback(async () => {
        if (!clientToken) return false;

        try {
            const response = await fetch('/api/client/auth/refresh', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${clientToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) return false;

            const data = await response.json();
            if (data.token) {
                localStorage.setItem('clientToken', data.token);
                setClientToken(data.token);
                console.log('Token renovado exitosamente');
                return true;
            }

            return false;
        } catch (error) {
            console.error('Error al renovar token:', error);
            return false;
        }
    }, [clientToken]);

    const refreshClientStatus = useCallback(async () => {
        const token = localStorage.getItem('clientToken');
        if (!token) return null;

        try {
            console.log('ClientAuthContext - Refrescando sesión del cliente');
            const response = await fetch('/api/client/auth/refresh-session', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.client) {
                    console.log('ClientAuthContext - Sesión refrescada exitosamente. Estado:', data.client.status);
                    localStorage.setItem('clientData', JSON.stringify(data.client));
                    setClient(data.client);

                    // Refrescar también la lista de servicios asociados
                    try {
                        const servicesResponse = await fetch('/api/client/auth/services', {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });
                        if (servicesResponse.ok) {
                            const servicesData = await servicesResponse.json();
                            if (Array.isArray(servicesData)) {
                                localStorage.setItem('clientServices', JSON.stringify(servicesData));
                                setServices(servicesData);
                            }
                        }
                    } catch (servicesError) {
                        console.error('Error refreshing client services:', servicesError);
                    }

                    return data.client;
                }
            }
        } catch (error) {
            console.error('Error refreshing client session data:', error);
        }
        return null;
    }, []);

    // Refrescar la sesión y los servicios del cliente en segundo plano para evitar datos desactualizados
    useEffect(() => {
        if (clientToken) {
            refreshClientStatus();
        }
    }, [clientToken, refreshClientStatus]);

    const value = {
        client,
        clientToken,
        services,
        loading,
        error: authError,
        loginClient,
        logoutClient,
        switchActiveService,
        updateServiceAlias,
        refreshToken,
        refreshClientStatus,
        isAuthenticated: !!clientToken,
        isActive: client?.status === 'active'
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                background: '#f8fafc',
                fontFamily: '"Outfit", "Inter", sans-serif'
            }}>
                <FiberLoader text="Iniciando conexión..." subtext="Cargando portal de fibra óptica..." fullPage />
            </div>
        );
    }

    return (
        <ClientAuthContext.Provider value={value}>
            {children}
        </ClientAuthContext.Provider>
    );
};

export const useClientAuth = () => {
    const context = useContext(ClientAuthContext);
    if (!context) {
        throw new Error('useClientAuth debe ser usado dentro de un ClientAuthProvider');
    }
    return context;
};